import {
  addHours,
  adjustToTimeZone,
  dispatchStatus,
  DispatchEntity,
  DispatchPatchDTO,
  PersonEntity,
  ShiftEntity,
  startOfDay,
} from "@ots-frontend/ots-data";
import React, {forwardRef, useCallback, useMemo, useState} from "react";
import {createPortal} from "react-dom";
import {useMantineTheme, Box, Card, Collapse, Group, ScrollArea, Stack, Text} from "@mantine/core";
import {EMPTY_ARRAY} from "../base/useDispatchDataGrid";
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
  type DraggableProvided,
  type DraggableStateSnapshot,
} from "react-beautiful-dnd";
// eslint-disable-next-line @nx/enforce-module-boundaries -- Reuse shared themed date and status foundations.
import {OtsDateTime, OtsBadge, OtsCheckbox, OtsIconActionButton, OtsThemeRoot} from "@ots-frontend/ots-ui-tw";
import {FormattedDatabaseCode} from "../../../text/FormattedDatabaseCode/FormattedDatabaseCode";
import {DispatchDataGridContextMenu, DispatchDataGridContextMenuItem} from "../menu/DispatchDataGridContextMenu";
import {useDispatchDataGridContextMenu} from "../menu/useDispatchDataGridContextMenu";
import {EntityMutationCallback} from "../../../../hooks/useEntityMutation";
import {IconArrowsSplit, IconChevronLeft, IconChevronRight} from "@tabler/icons-react";
import {useOtsColorScheme} from "../../DataGrid/dataGridTheme";
import {getDispatchDataGridRowTheme} from "../dispatchGridTheme";
import {getDriverShifts, getShiftContextFromShifts} from "../shift-context";

const UNASSIGNED_LANE_ID = "unassigned";
const FIFTEEN_MINUTES = 15 * 60 * 1000;
const TIME_BLOCK_DROPPABLE_PREFIX = "time-block";
const DROPPABLE_DELIMITER = "::";

export type DispatchBlock = {
  id: string;
  name: string;
  dispatches: DispatchEntity[];
};

type DispatchTimelineLane = DispatchBlock & {
  driverId?: string;
  shifts?: ShiftEntity[];
};

type TimeBlock = {
  id: string;
  startDttm: number;
  endDttm: number;
  label: string;
};

type ParsedDroppableId = {
  laneId: string;
  timeBlockStartDttm?: number;
};

export type DispatchBlockDataGridProps = {
  persons?: PersonEntity[];
  data?: DispatchEntity[];
  shifts?: ShiftEntity[];
  patchDispatch?: (dispatchId: string, patch: DispatchPatchDTO, callback: EntityMutationCallback | undefined) => void;
  onItemClick?: (item: DispatchDataGridContextMenuItem, data: DispatchEntity[]) => void;
  appDttm?: number;
};

const compareDispatches = (a: DispatchEntity, b: DispatchEntity): number => {
  const aOrder = a.performOrder;
  const bOrder = b.performOrder;

  if (typeof aOrder === "number" && typeof bOrder === "number" && aOrder !== bOrder) {
    return aOrder - bOrder;
  }

  if (typeof aOrder === "number") {
    return -1;
  }

  if (typeof bOrder === "number") {
    return 1;
  }

  return (a.scheduleDttm ?? 0) - (b.scheduleDttm ?? 0) || a.id.localeCompare(b.id);
};

const compareDispatchesBySchedule = (a: DispatchEntity, b: DispatchEntity): number => {
  return (a.scheduleDttm ?? 0) - (b.scheduleDttm ?? 0) || a.id.localeCompare(b.id);
};

const getTimeLabel = (dttm: number): string => {
  return new Intl.DateTimeFormat(undefined, {
    hour: "numeric",
    minute: "2-digit",
    hour12: false,
  }).format(dttm);
};

const buildTimeBlocks = (appDttm?: number): TimeBlock[] => {
  const selectedDate = typeof appDttm === "number" ? new Date(appDttm) : new Date();
  const dayStart = startOfDay(selectedDate).getTime();

  return [0, 6, 12, 18].map((hour) => {
    const startDttm = addHours(startOfDay(selectedDate), hour).getTime();
    const endDttm =
      hour === 18 ? dayStart + 24 * 60 * 60 * 1000 : addHours(startOfDay(selectedDate), hour + 6).getTime();

    return {
      id: String(startDttm),
      startDttm: adjustToTimeZone(startDttm).getTime(),
      endDttm: adjustToTimeZone(endDttm).getTime(),
      label: `${getTimeLabel(startDttm)} - ${getTimeLabel(endDttm)}`,
    };
  });
};

const buildTimeBlockDroppableId = (driverId: string | undefined, timeBlockStartDttm: number): string => {
  return [TIME_BLOCK_DROPPABLE_PREFIX, driverId, timeBlockStartDttm].join(DROPPABLE_DELIMITER);
};

const parseDroppableId = (droppableId: string): ParsedDroppableId => {
  if (droppableId === UNASSIGNED_LANE_ID) {
    return {laneId: UNASSIGNED_LANE_ID};
  }

  const [prefix, laneId, timeBlockStartDttm] = droppableId.split(DROPPABLE_DELIMITER);
  if (prefix === TIME_BLOCK_DROPPABLE_PREFIX && laneId && timeBlockStartDttm) {
    return {
      laneId,
      timeBlockStartDttm: Number(timeBlockStartDttm),
    };
  }

  return {laneId: droppableId};
};

const getDispatchesForTimeBlock = (dispatches: DispatchEntity[], timeBlock: TimeBlock): DispatchEntity[] => {
  const timeBlockedDispatches: DispatchEntity[] = [];

  for (const dispatch of dispatches) {
    if (dispatch.scheduleDttm) {
      if (dispatch.scheduleDttm < timeBlock.startDttm) {
        continue;
      }

      if (dispatch.scheduleDttm >= timeBlock.endDttm) {
        continue;
      }

      timeBlockedDispatches.push(dispatch);
    }
  }

  return timeBlockedDispatches.sort(compareDispatches);
};

const buildLanes = (
  persons: PersonEntity[],
  dispatches: DispatchEntity[],
  shifts: ShiftEntity[],
  appDttm?: number
): DispatchTimelineLane[] => {
  const lanes: DispatchTimelineLane[] = [
    {
      id: UNASSIGNED_LANE_ID,
      name: "Unassigned",
      dispatches: [],
    },
  ];

  const sortedPersons = [...persons].sort((a, b) => (a.nameReverse || "").localeCompare(b.nameReverse || ""));

  for (const person of sortedPersons) {
    lanes.push({
      id: person.id,
      driverId: person.id,
      name: person.nameReverse,
      shifts: getDriverShifts(person.id, shifts, appDttm),
      dispatches: [],
    });
  }

  for (const dispatch of dispatches) {
    const laneId = dispatch.personId ?? UNASSIGNED_LANE_ID;
    let lane = lanes.find((l) => l.id === laneId);

    if (!lane) {
      lane = {
        id: laneId,
        driverId: laneId,
        name: dispatch.personName ?? "Unknown Driver",
        shifts: getDriverShifts(laneId, shifts, appDttm),
        dispatches: [],
      };
      lanes.push(lane);
    }

    lane.dispatches.push(dispatch);
  }

  return lanes.map((lane) => ({
    ...lane,
    dispatches: [...lane.dispatches].sort(compareDispatches),
  }));
};

const patchValue = <T,>(value: T | undefined) => {
  return typeof value === "undefined" ? {present: true} : {present: true, value: value};
};

const getScheduleDttmForPosition = (
  dispatches: DispatchEntity[],
  movedDispatch: DispatchEntity,
  newIndex: number,
  fallbackDttm?: number,
  minDttm?: number,
  maxDttm?: number
): number | undefined => {
  const beforeDttm = newIndex > 0 ? dispatches[newIndex - 1].scheduleDttm : undefined;
  const afterDttm = newIndex < dispatches.length - 1 ? dispatches[newIndex + 1].scheduleDttm : undefined;
  let scheduleDttm: number | undefined;

  if (typeof beforeDttm === "number" && typeof afterDttm === "number") {
    if (beforeDttm + FIFTEEN_MINUTES < afterDttm) {
      scheduleDttm = beforeDttm + FIFTEEN_MINUTES;
    } else {
      scheduleDttm = Math.floor((beforeDttm + afterDttm) / 2);
    }
  } else if (typeof beforeDttm === "number") {
    scheduleDttm = beforeDttm + FIFTEEN_MINUTES;
  } else if (typeof afterDttm === "number") {
    scheduleDttm = afterDttm - FIFTEEN_MINUTES;
  } else {
    scheduleDttm = fallbackDttm ?? movedDispatch.scheduleDttm;
  }

  if (typeof scheduleDttm !== "number") {
    return undefined;
  }

  if (typeof minDttm === "number" && scheduleDttm < minDttm) {
    return minDttm;
  }

  if (typeof maxDttm === "number" && scheduleDttm >= maxDttm) {
    return maxDttm - 1;
  }

  return scheduleDttm;
};

const getPerformOrderPatches = (dispatches: DispatchEntity[]): {id: string; patch: DispatchPatchDTO}[] => {
  return dispatches
    .map((dispatch, index) => ({
      id: dispatch.id,
      patch: {
        performOrder: patchValue(index + 1),
      },
    }))
    .filter(({id, patch}, index) => dispatches[index].performOrder !== patch.performOrder?.value);
};

type DispatchCardProps = {
  dispatch: DispatchEntity;
  provided: DraggableProvided;
  snapshot: DraggableStateSnapshot;
  onSelectDispatch: (dispatch: DispatchEntity) => void;
  selectedDispatches?: DispatchEntity[];
  onContextMenu: (dispatchId: string, event: React.MouseEvent) => void;
  compact?: boolean;
};

const DispatchCard = (props: DispatchCardProps) => {
  const {
    dispatch,
    provided,
    snapshot,
    onSelectDispatch,
    selectedDispatches = EMPTY_ARRAY,
    onContextMenu,
    compact,
  } = props;
  const selected = selectedDispatches.findIndex((d) => d.id === dispatch.id) !== -1;
  const expanded = !compact || selected;
  const theme = useMantineTheme();
  const scheme = useOtsColorScheme() ?? theme.colorScheme;
  const cardTheme = getDispatchDataGridRowTheme(dispatch, scheme);
  const card = (
    <Card
      shadow={snapshot.isDragging ? "md" : "xs"}
      p={expanded ? "xs" : 8}
      withBorder
      onContextMenu={(e) => onContextMenu(dispatch.id, e)}
      onClick={() => onSelectDispatch(dispatch)}
      ref={provided.innerRef}
      {...provided.draggableProps}
      {...provided.dragHandleProps}
      style={{
        ...provided.draggableProps.style,
        width: compact || snapshot.isDragging ? 260 : "100%",
        maxWidth: "100%",
        minHeight: expanded ? 112 : 78,
        boxSizing: "border-box",
        cursor: "pointer",
        background: cardTheme?.bgCell ?? "var(--ots-surface)",
        color: cardTheme?.textDark ?? "var(--ots-text)",
        borderColor: selected ? "var(--ots-primary)" : "var(--ots-outline-variant)",
        zIndex: snapshot.isDragging ? 1000 : undefined,
      }}
    >
      <Stack spacing={expanded ? 6 : 4}>
        <Group position="apart" noWrap>
          <Group spacing="xs" noWrap>
            <OtsCheckbox
              aria-label={`Select dispatch ${dispatch.workOrderNumber ?? dispatch.id}`}
              checked={selected}
              onChange={() => onSelectDispatch(dispatch)}
              onClick={(e) => e.stopPropagation()}
              size={expanded ? "default" : "compact"}
            />
            <Text size={expanded ? "sm" : "xs"} weight={700} lineClamp={1}>
              {dispatch.details}
            </Text>
          </Group>
          <OtsBadge
            size="compact"
            tone={
              dispatch.status === dispatchStatus.canceled
                ? "danger"
                : dispatch.status === dispatchStatus.completed
                ? "success"
                : dispatch.status === dispatchStatus.started
                ? "warning"
                : "info"
            }
          >
            <FormattedDatabaseCode value={dispatch.status} />
          </OtsBadge>
        </Group>

        <Text size={expanded ? "sm" : "xs"} lineClamp={1}>
          {dispatch.pickupSiteName} -&gt; {dispatch.deliverySiteName}
        </Text>
        {expanded && dispatch.pickupContainerName && (
          <Text size="xs" style={{color: "var(--ots-text-muted)"}} lineClamp={1}>
            {dispatch.pickupContainerName}
          </Text>
        )}

        <Group position="apart" spacing="xs" noWrap>
          <Group spacing={6} noWrap>
            {dispatch.splitGroupId && <IconArrowsSplit size={14} />}
            <Text size="xs" style={{color: cardTheme?.textMedium ?? "var(--ots-text-muted)"}}>
              <OtsDateTime value={dispatch.scheduleDttm} />
            </Text>
          </Group>
          {typeof dispatch.performOrder === "number" && <OtsBadge size="compact">#{dispatch.performOrder}</OtsBadge>}
        </Group>
      </Stack>
    </Card>
  );

  return snapshot.isDragging && typeof document !== "undefined"
    ? createPortal(<OtsThemeRoot colorScheme={scheme}>{card}</OtsThemeRoot>, document.body)
    : card;
};

type LaneProps = {
  lane: DispatchTimelineLane;
  selectedDispatches: DispatchEntity[];
  onSelectDispatch: (dispatch: DispatchEntity) => void;
  onContextMenu: (dispatchId: string, event: React.MouseEvent) => void;
};

const DispatchCardStack = (
  props: LaneProps & {
    dispatches?: DispatchEntity[];
    droppableId: string;
    minHeight?: number;
    compact?: boolean;
    dropOnly?: boolean;
  }
) => {
  const {
    lane,
    dispatches = lane.dispatches,
    droppableId,
    selectedDispatches,
    onSelectDispatch,
    onContextMenu,
    minHeight = 148,
    compact,
    dropOnly,
  } = props;

  return (
    <Droppable droppableId={droppableId} direction="vertical">
      {(provided, snapshot) => (
        <Box
          ref={provided.innerRef}
          {...provided.droppableProps}
          p="sm"
          style={{
            minHeight: minHeight,
            background: snapshot.isDraggingOver ? "var(--ots-blue-container, #e7f5ff)" : undefined,
            overflowY: "auto",
          }}
        >
          {dropOnly ? (
            <Text size="xs" style={{color: "var(--ots-text-muted)"}} align="center">
              Drop
            </Text>
          ) : (
            <Stack spacing="sm">
              {dispatches.map((dispatch, index) => (
                <Draggable key={dispatch.id} draggableId={dispatch.id} index={index}>
                  {(provided, snapshot) => (
                    <DispatchCard
                      dispatch={dispatch}
                      provided={provided}
                      snapshot={snapshot}
                      onSelectDispatch={onSelectDispatch}
                      selectedDispatches={selectedDispatches}
                      onContextMenu={onContextMenu}
                      compact={compact}
                    />
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </Stack>
          )}
          {dropOnly && provided.placeholder}
        </Box>
      )}
    </Droppable>
  );
};

const TimeBlockDispatchStack = (props: LaneProps & {timeBlock: TimeBlock}) => {
  const {lane, timeBlock, ...rest} = props;
  const dispatches = useMemo(() => getDispatchesForTimeBlock(lane.dispatches, timeBlock), [lane.dispatches, timeBlock]);

  return (
    <Box style={{borderLeft: "1px solid var(--ots-outline-variant, #e9ecef)", minHeight: 148}}>
      <Text
        size="xs"
        weight={700}
        p="xs"
        style={{
          color: "var(--ots-text-muted)",
          borderBottom: "1px solid var(--ots-outline-variant, #e9ecef)",
          background: "var(--ots-surface-variant, #f8f9fa)",
        }}
      >
        {timeBlock.label}
      </Text>
      <DispatchCardStack
        lane={lane}
        dispatches={dispatches}
        droppableId={buildTimeBlockDroppableId(lane.driverId ?? lane.id, timeBlock.startDttm)}
        minHeight={116}
        compact
        {...rest}
      />
    </Box>
  );
};

const TimelineHeader = (props: {timeBlocks: TimeBlock[]}) => {
  const {timeBlocks} = props;

  return (
    <Box
      style={{
        display: "grid",
        gridTemplateColumns: `240px repeat(${timeBlocks.length}, 300px)`,
        borderBottom: "2px solid var(--ots-outline, #dee2e6)",
        position: "sticky",
        top: 0,
        zIndex: 2,
        background: "var(--ots-surface, #ffffff)",
      }}
    >
      <Box
        p="xs"
        style={{
          borderRight: "2px solid var(--ots-outline-variant, #e9ecef)",
          background: "var(--ots-surface-variant, #f8f9fa)",
        }}
      >
        <Text size="xs" weight={700} style={{color: "var(--ots-text-muted)"}}>
          Driver
        </Text>
      </Box>
      {timeBlocks.map((timeBlock) => (
        <Box
          key={timeBlock.id}
          p="xs"
          style={{
            borderLeft: "1px solid var(--ots-outline-variant, #e9ecef)",
            background: "var(--ots-surface-variant, #f8f9fa)",
          }}
        >
          <Text size="xs" weight={700} style={{color: "var(--ots-text-muted)"}}>
            {timeBlock.label}
          </Text>
        </Box>
      ))}
    </Box>
  );
};

const ShiftSummary = (props: {shifts?: ShiftEntity[]; appDttm?: number}) => {
  const {shifts = []} = props;
  const shiftContext = getShiftContextFromShifts(shifts);

  if (shiftContext.shifts.length === 0) {
    return <OtsBadge size="compact">Off Shift</OtsBadge>;
  }

  return (
    <Stack spacing={4} mt={6}>
      <Group spacing="xs" noWrap>
        <OtsBadge size="compact" tone={shiftContext.active ? "success" : "info"}>
          {shiftContext.status}
        </OtsBadge>
        {shiftContext.primaryShift?.vehicleName && (
          <Text size="xs" style={{color: "var(--ots-text-muted)"}} lineClamp={1}>
            {shiftContext.primaryShift.vehicleName}
          </Text>
        )}
      </Group>
      {shifts.slice(0, 2).map((shift) => (
        <Text key={shift.id} size="xs" style={{color: "var(--ots-text-muted)"}} lineClamp={1}>
          <OtsDateTime value={shift.startDttm} /> -{" "}
          {typeof shift.endDttm === "number" ? <OtsDateTime value={shift.endDttm} /> : "Open"}
        </Text>
      ))}
      {shifts.length > 2 && (
        <Text size="xs" style={{color: "var(--ots-text-muted)"}}>
          +{shifts.length - 2} more
        </Text>
      )}
    </Stack>
  );
};

const TimelineLane = (props: LaneProps & {timeBlocks: TimeBlock[]; appDttm?: number}) => {
  const {lane, timeBlocks, appDttm} = props;

  return (
    <Box
      style={{
        display: "grid",
        gridTemplateColumns: `240px repeat(${timeBlocks.length}, 300px)`,
        minHeight: 148,
        borderBottom: "2px solid var(--ots-outline-variant, #e9ecef)",
      }}
    >
      <Box
        p="sm"
        style={{
          borderRight: "2px solid var(--ots-outline-variant, #e9ecef)",
          background: "var(--ots-surface-variant, #f8f9fa)",
        }}
      >
        <Text size="sm" weight={700}>
          {lane.name}
        </Text>
        <Text size="xs" style={{color: "var(--ots-text-muted)"}}>
          {lane.dispatches.length} dispatches
        </Text>
        <ShiftSummary shifts={lane.shifts} appDttm={appDttm} />
      </Box>

      {timeBlocks.map((timeBlock) => (
        <TimeBlockDispatchStack key={`${lane.id}-${timeBlock.id}`} {...props} timeBlock={timeBlock} />
      ))}
    </Box>
  );
};

const UnassignedPanel = (props: LaneProps & {collapsed: boolean; onToggle: () => void}) => {
  const {lane, collapsed, onToggle, ...rest} = props;

  return (
    <Card
      shadow="xs"
      radius={0}
      withBorder
      style={{
        width: collapsed ? 96 : 340,
        flex: "0 0 auto",
        transition: "width 160ms ease",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        borderRight: "2px solid var(--ots-outline, #dee2e6)",
        background: "var(--ots-surface)",
        color: "var(--ots-text)",
      }}
    >
      <Group position="apart" noWrap mb="xs">
        {!collapsed && (
          <Text size="sm" weight={700}>
            Unassigned
          </Text>
        )}
        <OtsBadge size="compact" tone="warning">
          {lane.dispatches.length}
        </OtsBadge>
        <OtsIconActionButton
          size="mini"
          variant="text"
          label={collapsed ? "Expand unassigned" : "Collapse unassigned"}
          onClick={onToggle}
          icon={collapsed ? <IconChevronRight /> : <IconChevronLeft />}
        />
      </Group>
      <Collapse in={!collapsed}>
        <Text size="xs" style={{color: "var(--ots-text-muted)"}} mb="xs">
          Drop dispatches here to unassign.
        </Text>
      </Collapse>
      <Box style={{flex: 1, minHeight: collapsed ? 140 : undefined}}>
        <ScrollArea style={{height: "100%"}}>
          <DispatchCardStack
            lane={lane}
            droppableId={UNASSIGNED_LANE_ID}
            minHeight={collapsed ? 140 : 240}
            dropOnly={collapsed}
            {...rest}
          />
        </ScrollArea>
      </Box>
    </Card>
  );
};

export const EMPTY_PERSON_ARRAY: PersonEntity[] = [];

export const DispatchBlockDataGrid = forwardRef<HTMLDivElement, DispatchBlockDataGridProps>((props, ref) => {
  const {persons = EMPTY_PERSON_ARRAY, data = EMPTY_ARRAY, shifts = [], patchDispatch, onItemClick, appDttm} = props;

  const theme = useMantineTheme();
  const scheme = useOtsColorScheme() ?? theme.colorScheme;
  const [selectedDispatches, setSelectedDispatches] = useState<DispatchEntity[]>([]);
  const [unassignedCollapsed, setUnassignedCollapsed] = useState(false);

  const lanes = useMemo(() => buildLanes(persons, data, shifts, appDttm), [appDttm, persons, data, shifts]);
  const unassignedLane = lanes[0];
  const driverLanes = lanes.slice(1);
  const timeBlocks = useMemo(() => buildTimeBlocks(appDttm), [appDttm]);
  const timeBlockLookup = useMemo(() => {
    return new Map(timeBlocks.map((timeBlock) => [timeBlock.startDttm, timeBlock]));
  }, [timeBlocks]);

  const dispatchLookup = useMemo(() => {
    return new Map(data.map((dispatch) => [dispatch.id, dispatch]));
  }, [data]);

  const {isOpen, renderLayer, layerProps, contextMenu, onContextMenu} = useDispatchDataGridContextMenu({
    dispatches: data,
    selectedDispatches: selectedDispatches,
  });

  const onSelectDispatch = useCallback((dispatch: DispatchEntity) => {
    setSelectedDispatches((prev) => {
      const included = prev.findIndex((d) => d.id === dispatch.id) !== -1;
      return included ? prev.filter((d) => d.id !== dispatch.id) : [...prev, dispatch];
    });
  }, []);

  const onDragEnd = useCallback(
    (result: DropResult) => {
      const {source, destination, draggableId} = result;
      if (!destination || !patchDispatch) {
        return;
      }

      if (unassignedCollapsed && destination.droppableId === UNASSIGNED_LANE_ID) {
        setUnassignedCollapsed(false);
      }

      if (source.droppableId === destination.droppableId && source.index === destination.index) {
        return;
      }

      const sourceDrop = parseDroppableId(source.droppableId);
      const destinationDrop = parseDroppableId(destination.droppableId);
      const sourceLane = lanes.find((lane) => lane.id === sourceDrop.laneId);
      const destinationLane = lanes.find((lane) => lane.id === destinationDrop.laneId);
      const movedDispatch = dispatchLookup.get(draggableId);

      if (!sourceLane || !destinationLane || !movedDispatch) {
        return;
      }

      const getDroppableDispatches = (lane: DispatchTimelineLane, drop: ParsedDroppableId): DispatchEntity[] => {
        if (typeof drop.timeBlockStartDttm === "number") {
          const timeBlock = timeBlockLookup.get(drop.timeBlockStartDttm);
          return timeBlock ? getDispatchesForTimeBlock(lane.dispatches, timeBlock) : [];
        }

        return [...lane.dispatches].sort(compareDispatches);
      };

      const sourceDispatches = getDroppableDispatches(sourceLane, sourceDrop);
      const destinationDispatches =
        source.droppableId === destination.droppableId
          ? sourceDispatches
          : getDroppableDispatches(destinationLane, destinationDrop).filter(
              (dispatch) => dispatch.id !== movedDispatch.id
            );
      const [removedDispatch] = sourceDispatches.splice(source.index, 1);
      const dispatchToInsert = removedDispatch ?? movedDispatch;
      destinationDispatches.splice(destination.index, 0, dispatchToInsert);

      const patches = new Map<string, DispatchPatchDTO>();
      const mergePatch = (dispatchId: string, patch: DispatchPatchDTO) => {
        patches.set(dispatchId, {
          ...patches.get(dispatchId),
          ...patch,
        });
      };

      if (destinationLane.id === UNASSIGNED_LANE_ID) {
        mergePatch(movedDispatch.id, {
          personId: {present: true},
        });
      } else if (movedDispatch.personId !== destinationLane.driverId) {
        mergePatch(movedDispatch.id, {
          personId: patchValue(destinationLane.driverId),
        });
      }

      const destinationTimeBlock =
        typeof destinationDrop.timeBlockStartDttm === "number"
          ? timeBlockLookup.get(destinationDrop.timeBlockStartDttm)
          : undefined;

      const fallbackScheduleDttm = destinationTimeBlock
        ? Math.min(
            destinationTimeBlock.startDttm + destination.index * FIFTEEN_MINUTES,
            destinationTimeBlock.endDttm - 1
          )
        : appDttm;
      const newScheduleDttm = destinationTimeBlock
        ? getScheduleDttmForPosition(
            destinationDispatches,
            movedDispatch,
            destination.index,
            fallbackScheduleDttm,
            destinationTimeBlock.startDttm,
            destinationTimeBlock.endDttm
          )
        : undefined;

      if (typeof newScheduleDttm === "number" && newScheduleDttm !== movedDispatch.scheduleDttm) {
        mergePatch(movedDispatch.id, {
          scheduleDttm: patchValue(newScheduleDttm),
        });
      }

      const getProjectedLaneDispatches = (
        lane: DispatchTimelineLane,
        projectedDispatch?: DispatchEntity,
        projectedBlockDispatches?: DispatchEntity[],
        projectedBlock?: TimeBlock,
        removedDispatchId?: string
      ): DispatchEntity[] => {
        const projectedDispatchId = removedDispatchId ?? projectedDispatch?.id;
        const laneDispatches = [
          ...lane.dispatches.filter((dispatch) => dispatch.id !== projectedDispatchId),
          ...(projectedDispatch ? [projectedDispatch] : []),
        ];

        const laneDispatchLookup = new Map(laneDispatches.map((dispatch) => [dispatch.id, dispatch]));
        const orderedDispatches: DispatchEntity[] = [];
        const usedDispatchIds = new Set<string>();

        for (const timeBlock of timeBlocks) {
          const blockDispatches =
            projectedBlock && timeBlock.startDttm === projectedBlock.startDttm
              ? projectedBlockDispatches ?? []
              : getDispatchesForTimeBlock(laneDispatches, timeBlock).filter(
                  (dispatch) => dispatch.id !== projectedDispatchId
                );

          for (const dispatch of blockDispatches) {
            const updatedDispatch = laneDispatchLookup.get(dispatch.id) ?? dispatch;
            orderedDispatches.push(updatedDispatch);
            usedDispatchIds.add(dispatch.id);
          }
        }

        const remainingDispatches = laneDispatches
          .filter((dispatch) => !usedDispatchIds.has(dispatch.id))
          .sort(compareDispatchesBySchedule);

        return [...orderedDispatches, ...remainingDispatches];
      };

      if (destinationLane.id !== UNASSIGNED_LANE_ID) {
        const projectedDispatch = {
          ...movedDispatch,
          personId: destinationLane.driverId,
          scheduleDttm: newScheduleDttm ?? movedDispatch.scheduleDttm,
        } as DispatchEntity;
        const projectedBlockDispatches = destinationDispatches.map((dispatch) => {
          return dispatch.id === movedDispatch.id ? projectedDispatch : dispatch;
        });
        const destinationLaneDispatches = getProjectedLaneDispatches(
          destinationLane,
          projectedDispatch,
          projectedBlockDispatches,
          destinationTimeBlock
        );

        for (const {id, patch} of getPerformOrderPatches(destinationLaneDispatches)) {
          mergePatch(id, patch);
        }
      }

      if (sourceLane.id !== destinationLane.id && sourceLane.id !== UNASSIGNED_LANE_ID) {
        const sourceLaneDispatches = getProjectedLaneDispatches(
          sourceLane,
          undefined,
          undefined,
          undefined,
          movedDispatch.id
        );

        for (const {id, patch} of getPerformOrderPatches(sourceLaneDispatches)) {
          mergePatch(id, patch);
        }
      }

      for (const [dispatchId, patch] of patches) {
        patchDispatch(dispatchId, patch, undefined);
      }
    },
    [appDttm, dispatchLookup, lanes, patchDispatch, timeBlockLookup, timeBlocks, unassignedCollapsed]
  );

  const onContextMenuItemClick = useCallback(
    (item: DispatchDataGridContextMenuItem, data: DispatchEntity[]) => {
      if (onItemClick) {
        onItemClick(item, data);
      }

      if (item === "CLEAR") {
        setSelectedDispatches([]);
      }

      if (contextMenu?.onItemClick) {
        contextMenu.onItemClick();
      }
    },
    [contextMenu, onItemClick]
  );

  return (
    <OtsThemeRoot colorScheme={scheme} style={{height: "100%", minHeight: 0, minWidth: 0}}>
      <DragDropContext onDragEnd={onDragEnd}>
        <Box
          ref={ref}
          style={{
            height: "100%",
            display: "flex",
            minWidth: 0,
            border: "1px solid var(--ots-outline-variant)",
            background: "var(--ots-background)",
          }}
        >
          {unassignedLane && (
            <UnassignedPanel
              lane={unassignedLane}
              selectedDispatches={selectedDispatches}
              onSelectDispatch={onSelectDispatch}
              onContextMenu={onContextMenu}
              collapsed={unassignedCollapsed}
              onToggle={() => setUnassignedCollapsed((value) => !value)}
            />
          )}
          <ScrollArea style={{flex: 1}}>
            <Box style={{minWidth: 240 + timeBlocks.length * 300}}>
              <TimelineHeader timeBlocks={timeBlocks} />
              {driverLanes.map((lane) => (
                <TimelineLane
                  key={lane.id}
                  lane={lane}
                  timeBlocks={timeBlocks}
                  appDttm={appDttm}
                  selectedDispatches={selectedDispatches}
                  onSelectDispatch={onSelectDispatch}
                  onContextMenu={onContextMenu}
                />
              ))}
            </Box>
          </ScrollArea>

          {isOpen &&
            contextMenu &&
            renderLayer(
              <div {...layerProps}>
                <DispatchDataGridContextMenu data={contextMenu.data} onItemClick={onContextMenuItemClick} />
              </div>
            )}
        </Box>
      </DragDropContext>
    </OtsThemeRoot>
  );
});
